/**
 * Municipal Council Routes Configuration
 */

import { MODULE_HEADER_BG } from '@/theme/moduleHeader';
import React, { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { ModuleBannerProvider, type ModuleBanner } from '../../../../core/layout/moduleBanner';

// Setup and Reports draw the slim module masthead in the council violet. No UDA picker:
// council report SQL carries no uda filter yet, so a pick would change nothing.
const COUNCIL_BANNER_BG = MODULE_HEADER_BG;
const COUNCIL_SETUP_BANNER: ModuleBanner = { background: COUNCIL_BANNER_BG, title: 'Setup' };
const COUNCIL_REPORTS_BANNER: ModuleBanner = { background: COUNCIL_BANNER_BG, title: 'Reports', noUda: true };

const CouncilMeetingsDashboard = lazy(() => import('../pages/CouncilMeetingsDashboard'));
const MeetingsList = lazy(() => import('../pages/MeetingsList'));
const MembersPage = lazy(() => import('../pages/MembersPage'));
const ServiceRequests = lazy(() => import('../pages/ServiceRequests'));

// Masters
const CommitteeTypeManagement = lazy(() => import('../masters/committee-type'));
const CouncilCasteManagement = lazy(() => import('../masters/council-caste'));
const CouncilDesignationManagement = lazy(() => import('../masters/council-designation'));
const CouncilMeetingTypeManagement = lazy(() => import('../masters/council-meeting-type'));
const CouncilPartyManagement = lazy(() => import('../masters/council-party'));
const CouncilRouterManagement = lazy(() => import('../masters/council-router'));
const CouncilQualificationManagement = lazy(() => import('../masters/council-qualification'));
const PreambleList = lazy(() => import('../pages/PreambleList'));
const AgendaList = lazy(() => import('../pages/AgendaList'));
const MOMList = lazy(() => import('../pages/MOMList'));
const AttendanceList = lazy(() => import('../pages/AttendanceList'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const CouncilDataEntry = lazy(() => import('../pages/CouncilDataEntryList'));

// Import routes from core modules
import { reportsRoutes } from '@/modules/core/reports/routes';
import { setupRoutes } from '@/modules/core/setup/routes';

// Simple pass-through layout for nested routes
const PassThroughLayout = () => <Outlet />;

// Master routes for municipal council
const masterRoutes: RouteObject[] = [
  {
    path: 'committee-types',
    element: <CommitteeTypeManagement />
  },
  {
    path: 'castes',
    element: <CouncilCasteManagement />
  },
  {
    path: 'designations',
    element: <CouncilDesignationManagement />
  },
  {
    path: 'meeting-types',
    element: <CouncilMeetingTypeManagement />
  },
  {
    path: 'parties',
    element: <CouncilPartyManagement />
  },
  {
    path: 'routers',
    element: <CouncilRouterManagement />
  },
  {
    path: 'qualifications',
    element: <CouncilQualificationManagement />
  }
];

export const municipalCouncilRoutes: RouteObject[] = [
  {
    index: true,
    element: <CouncilMeetingsDashboard />
  },
  {
    path: 'meetings',
    element: <MeetingsList />
  },
  {
    path: 'members',
    element: <MembersPage />
  },
  {
    path: 'committee-members',
    // Committee Members is now part of the single Members page.
    element: <MembersPage />
  },
  {
    path: 'service-requests',
    element: <ServiceRequests />
  },
  {
    path: 'masters',
    element: <PassThroughLayout />,
    children: masterRoutes
  },
  {
    path: 'reports',
    element: <ModuleBannerProvider value={COUNCIL_REPORTS_BANNER}><PassThroughLayout /></ModuleBannerProvider>,
    children: reportsRoutes
  },
  {
    path: 'setup',
    element: <ModuleBannerProvider value={COUNCIL_SETUP_BANNER}><PassThroughLayout /></ModuleBannerProvider>,
    children: setupRoutes
  },
  // Setup routes (mapped from setup options)
  {
    path: 'setup/committee-types',
    element: <CommitteeTypeManagement />
  },
  {
    path: 'setup/castes',
    element: <CouncilCasteManagement />
  },
  {
    path: 'setup/designations',
    element: <CouncilDesignationManagement />
  },
  {
    path: 'setup/meeting-types',
    element: <CouncilMeetingTypeManagement />
  },
  {
    path: 'setup/parties',
    element: <CouncilPartyManagement />
  },
  {
    path: 'setup/routers',
    element: <CouncilRouterManagement />
  },
  {
    path: 'setup/qualifications',
    element: <CouncilQualificationManagement />
  },
  {
    path: 'setup/council-members',
    element: <MembersPage />
  },
  {
    path: 'preamble',
    element: <PreambleList />
  },
  {
    path: 'agenda',
    element: <AgendaList />
  },
  {
    path: 'mom',
    element: <MOMList />
  },
  {
    path: 'notifications',
    element: <NotificationsPage />
  },
  {
    path: 'attendance',
    element: <AttendanceList />
  },
  {
    path: 'data-entry',
    element: <CouncilDataEntry />
  }
];

export default municipalCouncilRoutes;
